// How the Ministry of Health governs the program: roles, rhythm, decision gates and the tools the
// Minister uses to see the program.

import { Link } from 'react-router-dom';
import { SectionTitle, SiteLayout } from '../ui/site.tsx';

const ROLES = [
  ['Minister of Health', 'Political ownership. Approves the pilot, the scale-up (gate G1) and any extension (gate X). Receives the monthly brief and chairs the quarterly review.', 'Monthly brief · quarterly review'],
  ['Secretary Health', 'Chairs the steering committee. Approves sites, targets and the data schedule; signs the contract.', 'Monthly steering committee'],
  ['Director General Health Services', 'Program owner. Nominates the program focal person and district focal persons; tracks delivery.', 'Fortnightly'],
  ['Clinical lead (nominated)', 'Signs off thresholds, triage rules and escalation pathways before go-live; chairs the clinical-safety review.', 'Before go-live · quarterly'],
  ['AJK IT Board', 'Confirms the hosting location and security review; agrees the integration boundary with the Health Information System.', 'Gate G2 · as required'],
  ['District Health Officers', 'District roll-out, site readiness and follow-up of urgent flags.', 'Weekly during the pilot'],
  ['Facility in-charges', 'Daily operation, flag dispositions and community awareness.', 'Daily'],
  ['MOVO-X delivery team', 'Project manager, field engineers, helpdesk and fleet monitoring; monthly performance report.', 'Daily · monthly report'],
];

const GATES = [
  ['G0 · Approval', 'Week 0', 'Minister approves Option A; Secretary signs; focal person and clinical lead nominated.'],
  ['Mobilisation', 'Weeks 1–9', 'Joint site selection, data schedule, threshold sign-off, delivery, installation and training.'],
  ['Go-live', 'Week 10', 'Day 1 of the 90-day operating pilot at 25 sites.'],
  ['Interim checkpoint', 'Week 16', 'Day 45 review by the steering committee.'],
  ['G1 · Scale decision', 'Weeks 22–23', 'Day-90 pilot review against the seven agreed measures; Minister decides on Phase 2.'],
  ['G2 · Integration', 'With Phase 2', 'HIS / DHIS2 integration scope, data-sharing agreement and support model agreed with the AJK IT Board.'],
  ['Waves to 100', 'Phase 2', 'Batches of kiosks, each with readiness, training and go-live.'],
  ['Q · Quarterly review', 'Every quarter', 'Performance and clinical-safety review chaired by the Minister.'],
];

export function MinistryPage() {
  return (
    <SiteLayout>
      <section className="band-dark hero">
        <div className="wrap">
          <div className="gov-kicker">Ministry &amp; governance</div>
          <h1>How the Ministry of Health <span className="gold">runs the program</span></h1>
          <p className="lede">
            The Department owns the program and its data; MOVO-X delivers and operates the technology. Clear roles, a fixed meeting rhythm and
            decision gates mean the Minister approves each step on evidence — and sees the whole network on one page every month.
          </p>
          <div className="hero-actions">
            <Link to="/brief" className="btn btn-gold btn-lg">See the Minister’s monthly brief</Link>
            <Link to="/dashboard" className="btn btn-outline-light btn-lg">Open the program dashboard</Link>
          </div>
        </div>
      </section>

      <section className="band">
        <div className="wrap">
          <SectionTitle kicker="Who does what">Roles and rhythm</SectionTitle>
          <div className="gtable-wrap">
            <table className="gtable">
              <thead><tr><th>Who</th><th>Responsibility</th><th>Rhythm</th></tr></thead>
              <tbody>{ROLES.map(([w, r, t]) => <tr key={w}><td><strong>{w}</strong></td><td>{r}</td><td>{t}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="band band-ivory2">
        <div className="wrap">
          <SectionTitle kicker="Decision gates">Every step approved on evidence</SectionTitle>
          <div className="timeline">
            {GATES.map(([g, when, what]) => (
              <div key={g} className="tl-item"><div className="tl-when">{when}</div><div className="tl-what"><strong>{g}</strong> — {what}</div></div>
            ))}
          </div>
        </div>
      </section>

      <section className="band">
        <div className="wrap">
          <SectionTitle kicker="The Minister’s view">What the Minister sees, and when</SectionTitle>
          <div className="card-grid">
            <Link to="/brief" className="gcard gold"><div className="num">MONTHLY</div><h3>Ministerial brief</h3><p>One printable A4 page: screenings, teleconsults, urgent flags and response time, district comparison, kiosk uptime and the items that need a decision.</p><div className="go">Open the brief →</div></Link>
            <Link to="/dashboard" className="gcard"><div className="num">ANY TIME</div><h3>Live program dashboard</h3><p>The whole program across ten districts, the hospital map, the seven pilot measures, flags awaiting disposition and the device fleet.</p><div className="go">Open the dashboard →</div></Link>
            <div className="gcard"><div className="num">QUARTERLY</div><h3>Performance and safety review</h3><p>Chaired by the Minister with the Secretary, DG Health, the clinical lead and the AJK IT Board, using the dashboard and the clinical-safety report.</p></div>
          </div>
        </div>
      </section>

      <section className="band band-ivory2">
        <div className="wrap">
          <SectionTitle kicker="Safeguards">Data, safety and accountability</SectionTitle>
          <div className="card-grid">
            <div className="gcard"><h3>Department owns the data</h3><p>All program data belongs to the Health Department. Hosting location is agreed with the AJK IT Board, including an in-country option; full export on exit.</p></div>
            <div className="gcard"><h3>Clinician in the loop</h3><p>The system supports decisions; it never diagnoses. Urgent flags need staff disposition, and any doctor who differs from the suggestion records a reason.</p></div>
            <div className="gcard"><h3>Every action audited</h3><p>Logins, lookups, exports, consult decisions and threshold approvals are recorded in a tamper-evident, verifiable audit trail.</p></div>
            <div className="gcard"><h3>Minimum identity data</h3><p>CNIC numbers are never stored in full — only a keyed hash for matching and a masked form for display.</p></div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
